export interface OfferRequest {
  orderId?: string;
  vendorId?: string;
  id: string;
  status: OfferStatus;
  isExpired: boolean;
}

export const OfferStatus = {
  Pending: 'Pending',
  InProgress: 'InProgress',
  Ready: 'Ready',
  Complete: 'Complete',
  NoOfferAvailable: 'NoOfferAvailable',
  RequiredBcTerms: 'RequiredBcTerms',
  RequiredAnafTerms: 'RequiredAnafTerms',
  ProcessingAnaf: 'ProcessingAnaf',
  ProcessingBc: 'ProcessingBc',
  KycRequired: 'KycRequired',
  Failed: 'Failed',
} as const;

export type OfferStatus = (typeof OfferStatus)[keyof typeof OfferStatus];

// Mirrors Volt2-API PreliminaryOfferStatus (POST wlc/offers/preliminary). The
// lender prices synchronously: on (Already)Generated the offer is in /loans/offers.
export const PreliminaryOfferStatus = {
  Generated: 'Generated',
  AlreadyGenerated: 'AlreadyGenerated',
  // Still processing, or an expired score was just re-requested (minutes)
  ScoreNotReady: 'ScoreNotReady',
  // No score is on its way: the user goes through the scoring flow
  ScoreRequired: 'ScoreRequired',
  NoBcScore: 'NoBcScore',
  IncomeRequired: 'IncomeRequired',
  BcTermsRequired: 'BcTermsRequired',
  FinalOfferExists: 'FinalOfferExists',
  UserBlocked: 'UserBlocked',
  Disabled: 'Disabled',
} as const;

export type PreliminaryOfferStatus =
  (typeof PreliminaryOfferStatus)[keyof typeof PreliminaryOfferStatus];

export interface PreliminaryOfferResponse {
  status: PreliminaryOfferStatus;
}

export interface MarketingValues {
  creditLimit: number;
  duration: number;
  minDailyPercentage: number;
  maxDailyPercentage: number;
  minTotalPercentage: number;
  maxTotalPercentage: number;
  minEar: number;
  maxEar: number;
}

export const OfferRejectionCode = {
  DtiRatioExceeded: 'dti_ratio_exceeded',
  IncomeBelowMinimum: 'income_below_minimum',
  IncomeNotVerified: 'income_not_verified',
  MaximumAgeExceeded: 'maximum_age_exceeded',
  PricingNotMatched: 'pricing_not_matched',
} as const;

export type OfferRejectionCode =
  (typeof OfferRejectionCode)[keyof typeof OfferRejectionCode];

export interface PeriodCreditLimits {
  loanDuration: number;
  creditLimit: number;
}

export interface CreditOffer {
  commission: number;
  id: string;
  creditLimit: number;
  creditProductName: OceanCreditProductName;
  subProductName: string;
  type: CreditProductType;
  clientCategory: number;
  clientRiskCategory: number;
  isAvailable: boolean;
  // false for a preliminary offer (declared income, before ANAF)
  isFinal: boolean;
  // Why the lender rejected the offer, see OfferRejectionCode
  rejectedCode?: string | null;
  localizedProductName: string;
  minAmount: number;
  maxAmount: number;
  effectiveAnnualPercentageRate: number;
  nominalAnnualPercentageRate: number;
  nominalMonthlyPercentageRate: number;
  numberOfRates: number;
  minNumberOfRates: number;
  minAvailableNumberOfRates: number;
  maxNumberOfRates: number;
  paymentScheduleDuration: number;
  productType: number;
  interestDailyPercentage: number;
  penaltyInterestDailyPercentage: number;
  costPerDay: number;
  prolongationFee: number;
  nominalAnnualPercentageRates: number | null;
  refinancedContractId: string | null;
  marketingValues: MarketingValues;
  grantingCommission: number;
  loanGrantingCommissionFixedAmount: number;
  loanGrantingCommissionPercentage: number;
  periodCreditLimits: PeriodCreditLimits[];
  isExternalRefinanceEnabled: boolean;
  creditOfferSource: number;
  creditOfferType: number;
  salary: number;
  monthlyPayments: number;
  degreeOfIndebtedness: number;
  isDerogationOffer: boolean;
  bnrLimitPercentage: number;
  order: number;
}

export const CreditProductType = {
  Consumer: 0,
  CreditLine: 1,
  Refinance: 2,
  WhiteLabelCard: 3,
};

export type CreditProductType =
  (typeof CreditProductType)[keyof typeof CreditProductType];

export interface LoanOfferState {
  // The scoring the offers were generated from
  id: string | null;
  bcFicoScore: number;
  anafProceed: boolean;
  timestamp: string;
  creditOffers: CreditOffer[];
  // Kept once the final offers are in, to compare them with what was chosen.
  preliminaryOffers?: CreditOffer[];
  alternativeCreditOffers: CreditOffer[];
  hasActiveContract: boolean;
  genericOffers: GenericOffer[];
  expiresAt: Date;
  proofOfIncomeRequired: boolean;
  proofOfIncomeProcessing: boolean;
}

export interface GenericOffer {
  id: string;
  maxAmount: number;
  durationMonths: number;
  creditProductName: OceanCreditProductName;
  type: CreditProductType;
  localizedProductName: string;
  minAmount: number;
}

export const OceanCreditProductName = {
  Penguin: 'Penguin',
  Crab: 'Crab',
  Dolphin: 'Dolphin',
  Refinance: 'Refinance',
  DolphinPlus: 'DolphinPlus',
  Coral: 'Coral',
} as const;

export type OceanCreditProductName =
  (typeof OceanCreditProductName)[keyof typeof OceanCreditProductName];

export interface ProductLoanDetails {
  id: string;
  selectedAmount: number;
  finalPaymentAmount: number;
  creditPrice: number;
  annualInterest: number;
  monthlyInterest: number;
  dae: number;
  interestDailyPercentage: number;
  penaltyDailyPercentage: number;
  creditLimit: number;
  loanDuration: number;
  isAvailable: boolean;
  isExpired?: boolean;
  commission?: number;
}

export interface InstallmentOfferDetails extends ProductLoanDetails {
  rates: Rate[];
  selectedNumberOfRates: number;
  grantingCommission: number;
  minAmount: number;
  maxAmount: number;
  minAvailableNumberOfRates: number;
  lastRateDate: Date | undefined;
  creditAmount: number;
  monthlyPayment: number | undefined;
  isDerogation: boolean;
}
export interface Rate {
  date: Date;
  amount: number;
}

export interface CreditLinePaymentExample {
  monthlyCostExample: number;
  minPaymentExample: number;
  interestExample: number;
}

export interface CreditLineOfferDetails {
  selectedAmount: number;
  firstBillDate: Date;
  finalDueDate: Date;
  isDerogation: boolean;
}

export const OfferDerogationReason = {
  Medical: 1,
  Study: 2,
  Funeral: 3,
  DegreeOfIndebtedness: 4,
} as const;

export type OfferDerogationReason =
  (typeof OfferDerogationReason)[keyof typeof OfferDerogationReason];
